import React, {
  useState, useEffect, useRef, useLayoutEffect, useId,
} from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * InfoTip — small `?` glyph that reveals a tooltip on hover/tap/focus.
 *
 * Walter 2026-05-09: shared component for the inline-help inventory in
 * docs/marketing/popup_help_inventory.md. Renders a small `?` in
 * var(--text-dimmer); tooltip pops in var(--surface2) with a max-width
 * of 280px. Body is markdown so multi-line copy with bullets, **bold**,
 * and `code` works without manual JSX. Auto-flips above / right when
 * the tip would overflow the viewport.
 *
 * Closes on: Esc, outside-tap, second tap, blur.
 *
 * Props:
 *   children — tooltip body (string; markdown allowed).
 *   label    — accessible name for the trigger (default: "more info").
 */
export default function InfoTip({ children, label = 'more info' }) {
  // Three sources keep the tooltip open:
  //   pinned  — toggled by click/tap (sticky until Esc/outside-tap/2nd tap)
  //   hovered — desktop hover; suppressed on touch-only devices so a
  //             synthetic mouseenter doesn't strand the tip open
  //   focused — keyboard focus on the trigger button
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [placement, setPlacement] = useState({ vertical: 'below', horizontal: 'left' });

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const tipId = useId();

  const hoverCapable = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover)').matches;

  const isOpen = pinned || hovered || focused;

  // Esc closes everything keeping it open. We also blur the trigger so
  // a still-focused button doesn't immediately re-pop the tooltip.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPinned(false);
        setFocused(false);
        try { triggerRef.current?.blur(); } catch (_) { /* noop */ }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Outside-tap clears the pinned state. Hover / focus states clear
  // naturally via their own mouseleave / blur handlers.
  useEffect(() => {
    if (!pinned) return undefined;
    const onDown = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setPinned(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [pinned]);

  // Auto-flip placement so the tip doesn't get clipped at the viewport
  // edge. We measure once after the tip mounts and adjust; the initial
  // frame renders at default below/left, which is correct for most call
  // sites (left-aligned controls in a modal). The re-render on overflow
  // is one frame and visually imperceptible.
  useLayoutEffect(() => {
    if (!isOpen || !tooltipRef.current || !triggerRef.current) return;
    const tipRect = tooltipRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;

    const fitsBelow = (vh - triggerRect.bottom - margin) >= tipRect.height;
    const fitsLeft  = (triggerRect.left + tipRect.width + margin) <= vw;

    setPlacement(prev => {
      const next = {
        vertical:   fitsBelow ? 'below' : 'above',
        horizontal: fitsLeft  ? 'left'  : 'right',
      };
      if (prev.vertical === next.vertical && prev.horizontal === next.horizontal) {
        return prev;
      }
      return next;
    });
  }, [isOpen]);

  const onClick = (e) => {
    // Stop propagation so the click doesn't bubble to a parent that
    // might dismiss its own popover (e.g. clicking the title-row tip
    // shouldn't be treated as a click on the modal title).
    e.stopPropagation();
    setPinned(prev => {
      const next = !prev;
      if (!next) {
        // Closing path — also clear focus so a still-focused trigger
        // doesn't re-open the tip via the focused state. Required for
        // the "second tap closes" rule on touch.
        try { triggerRef.current?.blur(); } catch (_) { /* noop */ }
        setFocused(false);
      }
      return next;
    });
  };

  // Hover on the wrapper, not the button, so moving from the button
  // into the popped tooltip (which is a child of the wrapper) doesn't
  // trigger a mouseleave and close the tip mid-read.
  const onMouseEnter = hoverCapable ? () => setHovered(true)  : undefined;
  const onMouseLeave = hoverCapable ? () => setHovered(false) : undefined;

  // Walter 2026-05-10: bumped from --text-dimmer to --text so the
  // glyph actually reads at row scale. The inventory doc originally
  // called for --text-dimmer but live preview was too faint. Hover /
  // active tints to --accent for interaction feedback.
  const active = hovered || pinned || focused;
  const glyphColor  = active ? 'var(--accent)' : 'var(--text)';
  const glyphBorder = active ? 'var(--accent)' : 'var(--text)';

  // Tooltip placement — picked at run-time so the tip never falls off
  // the viewport. Default is below + anchored to trigger's left edge.
  const tipStyle = {
    position: 'absolute',
    zIndex: 100,
    background: 'var(--surface2)',
    border: '1px solid var(--border-bright)',
    borderRadius: 'var(--radius)',
    color: 'var(--text)',
    fontFamily: 'var(--mono)',
    fontSize: 'calc(11px * var(--ui-scale, 1))',
    lineHeight: 1.5,
    padding: '8px 10px',
    boxShadow: 'var(--shadow-tooltip)',
    width: 'max-content',
    maxWidth: '280px',
    whiteSpace: 'normal',
    pointerEvents: 'auto',
    ...(placement.vertical === 'below'
      ? { top: 'calc(100% + 4px)', bottom: 'auto' }
      : { bottom: 'calc(100% + 4px)', top: 'auto' }),
    ...(placement.horizontal === 'left'
      ? { left: 0, right: 'auto' }
      : { right: 0, left: 'auto' }),
  };

  return (
    <span
      ref={wrapperRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        // Don't let the wrapper's hit area overflow into adjacent rows
        // visually — but the inline-flex preserves baseline-ish layout.
        lineHeight: 1,
      }}
    >
      <button
        type="button"
        ref={triggerRef}
        onClick={onClick}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={label}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? tipId : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'calc(13px * var(--ui-scale, 1))',
          height: 'calc(13px * var(--ui-scale, 1))',
          padding: 0,
          margin: 0,
          background: 'transparent',
          border: `1px solid ${glyphBorder}`,
          borderRadius: '50%',
          color: glyphColor,
          fontFamily: 'var(--mono)',
          fontSize: 'calc(9px * var(--ui-scale, 1))',
          fontWeight: 700,
          lineHeight: 1,
          cursor: 'pointer',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          flex: '0 0 auto',
          transition: 'color 0.12s, border-color 0.12s',
        }}
      >
        ?
      </button>
      {isOpen && (
        <div
          ref={tooltipRef}
          id={tipId}
          role="tooltip"
          style={tipStyle}
          // Clicks inside the tip body are content interactions, not
          // a request to dismiss — stop them bubbling to the document
          // outside-tap handler.
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ReactMarkdown
            components={{
              p:      ({ node, ...rest }) => <p style={{ margin: '0 0 6px' }} {...rest} />,
              ul:     ({ node, ...rest }) => <ul style={{ margin: '0 0 6px', paddingLeft: 16 }} {...rest} />,
              li:     ({ node, ...rest }) => <li style={{ margin: '0 0 4px' }} {...rest} />,
              strong: ({ node, ...rest }) => <strong style={{ color: 'var(--text)', fontWeight: 600 }} {...rest} />,
              code:   ({ node, ...rest }) => (
                <code
                  style={{
                    background: 'var(--surface)',
                    padding: '0 4px',
                    borderRadius: 2,
                    fontSize: '0.95em',
                  }}
                  {...rest}
                />
              ),
              a: ({ node, ...rest }) => (
                <a
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                  target="_blank"
                  rel="noreferrer"
                  {...rest}
                />
              ),
            }}
          >
            {typeof children === 'string' ? children : ''}
          </ReactMarkdown>
        </div>
      )}
    </span>
  );
}
